import React from "react";
import { Share2, Copy, Gift, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useReferralShare } from "../lib/referralShare";

/** Marketing-site Community block — "Share EVIQ with a friend".
 *  Referral-aware: subscribers browsing the site share their ?ref= link. */
export const ShareCommunity = () => {
  const share = useReferralShare();

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "EVIQ — Battery Intelligence", text: share.text, url: share.url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copy();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${share.text} ${share.url}`);
      toast.success(share.isReferral ? "Referral link copied — free month when they subscribe" : "Link copied");
    } catch { /* noop */ }
  };

  return (
    <section className="py-14 sm:py-20 px-5" data-testid="share-community-section">
      <div className="max-w-4xl mx-auto rounded-3xl border p-6 sm:p-10"
        style={{ borderColor: "rgba(0,229,255,0.22)", background: "rgba(6,14,10,0.9)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-2 inline-flex items-center gap-2" style={{ color: "#00E5FF" }}>
          <Users className="w-3 h-3" strokeWidth={2.4} /> Community
        </div>
        <h2 className="text-base md:text-lg text-white font-semibold">Share EVIQ with a friend</h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl leading-relaxed">
          {share.isReferral
            ? "You're a subscriber — your personal referral link is loaded below. When a friend joins, you earn a free month (one per quarter, up to four a year)."
            : "Know someone flying blind on their EV battery? Send them EVIQ — and if you subscribe, sharing earns you free months."}
        </p>
        {share.isReferral && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em]"
            style={{ border: "1px solid rgba(57,255,20,0.4)", color: "#39FF14", background: "rgba(57,255,20,0.07)" }}
            data-testid="share-community-referral-badge">
            <Gift className="w-3 h-3" strokeWidth={2.4} />
            Referral link active · {share.monthsEarned} free month{share.monthsEarned === 1 ? "" : "s"} earned
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button type="button" onClick={onShare} data-testid="share-community-button"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-black hover:opacity-90 transition-opacity"
            style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}>
            <Share2 className="w-4 h-4" strokeWidth={2.4} /> Share EVIQ
          </button>
          <button type="button" onClick={copy} data-testid="share-community-copy"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.1em] text-zinc-300 hover:text-white transition-colors"
            style={{ border: "1px solid rgba(255,255,255,0.14)" }}>
            <Copy className="w-3.5 h-3.5" /> Copy link
          </button>
          <Link to="/referrers" data-testid="share-community-leaderboard"
            className="font-mono text-[10px] uppercase tracking-wider text-[#FFB020] hover:text-white transition-colors">
            See top referrers →
          </Link>
        </div>
      </div>
    </section>
  );
};
